/**
 * FactuurScan - Factuur Scannen met AI
 * EXACT zoals Morris factuur_scan.html
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import {
  boekhoudingService,
  BoekstukType,
  Grootboekrekening,
  Relatie,
  RelatieType,
  OCRData,
} from '../../services/boekhouding.service';
import { getApiBaseUrl } from '../../services/api';

const FactuurScan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const boekstukType = (searchParams.get('type') || 'inkoop') as 'inkoop' | 'verkoop';
  const isVerkoop = boekstukType === 'verkoop';

  // State
  const [apiKeySet, setApiKeySet] = useState(false);
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState('');
  const [cropStatus, setCropStatus] = useState('');
  const [error, setError] = useState('');

  // File state
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [, setCroppedImageBase64] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<OCRData>({});
  const [grootboeken, setGrootboeken] = useState<Grootboekrekening[]>([]);
  const [relaties, setRelaties] = useState<Relatie[]>([]);
  const [selectedRelatieId, setSelectedRelatieId] = useState<number | null>(null);
  const [relatieSearch, setRelatieSearch] = useState('');
  const [showRelatieDropdown, setShowRelatieDropdown] = useState(false);
  const [volgendBoekstuknummer, setVolgendBoekstuknummer] = useState('');

  // OCR response state
  const [relatieMatch, setRelatieMatch] = useState<{ id: number; naam: string } | null>(null);
  const [, setGrootboekSuggestie] = useState<string | null>(null);
  const [factuurBestaat, setFactuurBestaat] = useState<any>(null);
  const [nieuweRelatieSuggestie, setNieuweRelatieSuggestie] = useState<any>(null);

  // Crop modal state (voor toekomstige implementatie)
  const [, setOriginalImageData] = useState<string | null>(null);

  // Nieuwe relatie modal
  const [showNieuweRelatieModal, setShowNieuweRelatieModal] = useState(false);
  const [nieuweRelatieNaam, setNieuweRelatieNaam] = useState('');
  const [nieuweRelatieEmail, setNieuweRelatieEmail] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Huidig jaar
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      // Check instellingen
      const instellingen = await boekhoudingService.getInstellingen();
      const apiKey = instellingen.find(i => i.key === 'anthropic_api_key');
      const ocrSetting = instellingen.find(i => i.key === 'ai_factuur_ocr');

      setApiKeySet(!!apiKey?.value);
      setOcrEnabled(ocrSetting?.value === '1');

      // Laad grootboeken
      const gbResult = await boekhoudingService.getGrootboekrekeningen();
      setGrootboeken(gbResult.items.filter(g => g.actief));

      // Laad relaties
      const relType = isVerkoop ? RelatieType.DEBITEUR : RelatieType.CREDITEUR;
      const relResult = await boekhoudingService.getRelaties({ type: relType });
      setRelaties(relResult.items);

      // Boekstuknummer wordt door de backend gegenereerd volgens instellingen
      setVolgendBoekstuknummer('');

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check bestandsgrootte (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Bestand is te groot. Maximum is 10 MB.');
      return;
    }

    setCurrentFile(file);
    setError('');
    setShowForm(false);
    setCropStatus('');
    setScanStatus('');

    const fileType = file.type;
    setIsPdf(fileType === 'application/pdf');

    if (fileType.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setPreviewUrl(dataUrl);
        setOriginalImageData(dataUrl);
        // Start scan automatisch
        scanFactuur(file);
      };
      reader.readAsDataURL(file);
    } else if (fileType === 'application/pdf') {
      setPreviewUrl(URL.createObjectURL(file));
      // Start scan voor PDF
      scanFactuur(file);
    }
  };

  const scanFactuur = async (file: File, perspectivePoints?: number[]) => {
    setScanning(true);
    setScanStatus('Bezig met scannen...');

    try {
      const response = await boekhoudingService.scanFactuur(file, boekstukType, perspectivePoints);

      if (response.crop_message) {
        if (response.auto_cropped) {
          setCropStatus(`✓ ${response.crop_message}`);
        } else {
          setCropStatus(`⚠ ${response.crop_message}`);
        }
      }

      if (response.cropped_image) {
        setCroppedImageBase64(response.cropped_image);
        setPreviewUrl(`data:image/jpeg;base64,${response.cropped_image}`);
      }

      if (response.success && response.data) {
        setFormData(response.data);
        setShowForm(true);

        // Relatie match
        if (response.relatie_match) {
          setRelatieMatch(response.relatie_match);
          setSelectedRelatieId(response.relatie_match.id);
          setRelatieSearch(response.relatie_match.naam);
        }

        // Grootboek suggestie
        if (response.grootboek_suggestie) {
          setGrootboekSuggestie(response.grootboek_suggestie);
          setFormData(prev => ({ ...prev, grootboek: response.grootboek_suggestie! }));
        }

        // Factuur bestaat check
        if (response.factuur_bestaat) {
          setFactuurBestaat(response.factuur_bestaat);
          if (response.factuur_bestaat.is_placeholder) {
            setScanStatus(`✓ Gegevens geextraheerd! Vervangt placeholder ${response.factuur_bestaat.boekstuknummer}`);
          } else {
            setScanStatus(`⚠ LET OP: Factuur lijkt al te bestaan! Factuurnr: ${response.factuur_bestaat.factuurnummer || '-'}, Datum: ${response.factuur_bestaat.datum}, Bedrag: €${response.factuur_bestaat.bedrag.toFixed(2)}`);
          }
        } else if (response.relatie_match) {
          setScanStatus(`✓ Gegevens geextraheerd! ${isVerkoop ? 'Klant' : 'Leverancier'}: ${response.relatie_match.naam}`);
        } else if (response.nieuwe_relatie_suggestie) {
          setNieuweRelatieSuggestie(response.nieuwe_relatie_suggestie);
          setScanStatus('✓ Gegevens geextraheerd!');
        } else {
          setScanStatus('✓ Gegevens geextraheerd!');
        }
      } else {
        setScanStatus(`✗ ${response.message || 'Kon geen gegevens extraheren'}`);
      }
    } catch (err: any) {
      console.error('Scan error:', err);
      const errorMsg = err?.message || String(err);
      setScanStatus(`✗ Fout bij scannen: ${errorMsg}`);
    } finally {
      setScanning(false);
    }
  };

  const resetUpload = () => {
    setCurrentFile(null);
    setPreviewUrl(null);
    setCroppedImageBase64(null);
    setOriginalImageData(null);
    setIsPdf(false);
    setShowForm(false);
    setScanStatus('');
    setCropStatus('');
    setError('');
    setFormData({});
    setRelatieMatch(null);
    setGrootboekSuggestie(null);
    setFactuurBestaat(null);
    setNieuweRelatieSuggestie(null);
    setSelectedRelatieId(null);
    setRelatieSearch('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.bedrag_incl) {
      setError('Bedrag incl. BTW is verplicht');
      return;
    }

    try {
      const boekingData = {
        jaar: huidigJaar,
        datum: formData.datum || new Date().toISOString().split('T')[0],
        boekstuk: boekstukType as BoekstukType,
        boekstuknummer: undefined,  // Backend genereert volgens instellingen
        omschrijving: formData.omschrijving || (isVerkoop ? formData.klant : formData.leverancier) || 'Gescande factuur',
        bedrag_excl: formData.bedrag_excl || 0,
        btw_percentage: formData.btw_percentage || 21,
        btw_bedrag: formData.btw_bedrag || 0,
        bedrag_incl: formData.bedrag_incl!,
        grootboeknaam: formData.grootboek || (isVerkoop ? 'Omzet diensten' : 'Inkoop algemeen'),
        relatie_id: selectedRelatieId || undefined,
        extern_factuurnummer: formData.factuurnummer,
      };

      const boeking = await boekhoudingService.createBoeking(boekingData);

      // Upload het gescande document bij de boeking
      if (currentFile && boeking?.id) {
        try {
          const docFormData = new FormData();
          docFormData.append('file', currentFile);
          const token = localStorage.getItem('access_token');
          await fetch(`${getApiBaseUrl()}/api/v1/boekhouding/boekingen/${boeking.id}/document`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: docFormData,
          });
        } catch (docErr) {
          console.error('Document upload failed:', docErr);
        }
      }

      navigate(`/boekhouding?boekstuk=${boekstukType}`);
    } catch (err) {
      console.error('Error saving:', err);
      setError('Fout bij opslaan');
    }
  };

  const berekenIncl = () => {
    const excl = formData.bedrag_excl || 0;
    const btw = formData.btw_bedrag || 0;
    setFormData(prev => ({ ...prev, bedrag_incl: excl + btw }));
  };

  const selectRelatie = (id: number | null, naam: string) => {
    setSelectedRelatieId(id);
    setRelatieSearch(naam);
    setShowRelatieDropdown(false);
  };

  const filterRelaties = () => {
    const search = relatieSearch.toLowerCase();
    return relaties.filter(r => r.naam.toLowerCase().includes(search));
  };

  const [loadingBedrijfsgegevens, setLoadingBedrijfsgegevens] = useState(false);

  const openNieuweRelatieModal = async () => {
    const naam = nieuweRelatieSuggestie?.naam || '';
    if (!naam) {
      setShowNieuweRelatieModal(true);
      return;
    }

    // Haal bedrijfsgegevens op via AI
    setLoadingBedrijfsgegevens(true);
    setScanStatus('✓ Gegevens geextraheerd! Bedrijfsgegevens opzoeken via AI...');

    try {
      const response = await boekhoudingService.zoekBedrijfsgegevens(naam);
      console.log('Bedrijfsgegevens response:', response);

      if (response.success && response.data) {
        // Update suggestie: web search data aanvullen, maar OCR data niet overschrijven met lege waarden
        setNieuweRelatieSuggestie((prev: any) => {
          const merged = { ...prev };
          for (const [key, value] of Object.entries(response.data)) {
            if (value) { // Alleen overschrijven als web search een waarde heeft
              merged[key] = value;
            }
          }
          return merged;
        });
        setNieuweRelatieNaam(response.data.naam || naam);
        // Gebruik OCR email als web search geen email heeft
        setNieuweRelatieEmail(response.data.email || nieuweRelatieSuggestie?.email || '');
      } else {
        setNieuweRelatieNaam(naam);
        // Behoud OCR email
        setNieuweRelatieEmail(nieuweRelatieSuggestie?.email || '');
      }

      setScanStatus('✓ Gegevens geextraheerd!');
    } catch (err) {
      console.error('Error fetching bedrijfsgegevens:', err);
      setNieuweRelatieNaam(naam);
      setNieuweRelatieEmail('');
      setScanStatus('✓ Gegevens geextraheerd!');
    } finally {
      setLoadingBedrijfsgegevens(false);
      setShowNieuweRelatieModal(true);
    }
  };

  const saveNieuweRelatie = async () => {
    if (!nieuweRelatieNaam.trim()) {
      alert('Vul een naam in');
      return;
    }

    try {
      const newRelatie = await boekhoudingService.createRelatie({
        naam: nieuweRelatieNaam.trim(),
        type: isVerkoop ? RelatieType.DEBITEUR : RelatieType.CREDITEUR,
        email: nieuweRelatieEmail.trim() || undefined,
        adres: nieuweRelatieSuggestie?.adres,
        postcode: nieuweRelatieSuggestie?.postcode,
        plaats: nieuweRelatieSuggestie?.plaats,
        telefoon: nieuweRelatieSuggestie?.telefoon,
        btw_nummer: nieuweRelatieSuggestie?.btw_nummer,
        kvk_nummer: nieuweRelatieSuggestie?.kvk_nummer,
      });

      setRelaties(prev => [...prev, newRelatie]);
      selectRelatie(newRelatie.id, newRelatie.naam);
      setShowNieuweRelatieModal(false);
      setScanStatus(`✓ Gegevens geextraheerd! ${isVerkoop ? 'Klant' : 'Leverancier'} ${newRelatie.naam} toegevoegd.`);
    } catch (err) {
      console.error('Error creating relatie:', err);
      alert('Fout bij toevoegen');
    }
  };

  const title = isVerkoop ? 'Verkoop Factuur Scannen met AI' : 'Factuur Scannen met AI';
  const relatieLabel = isVerkoop ? 'Klant' : 'Leverancier';

  if (loading) {
    return (
      <BoekhoudingLayout title={title}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title={title}>
      <div className={`${styles.card} ${styles.cardForm}`} style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Waarschuwingen */}
        {!apiKeySet && (
          <div className={styles.alertWarning}>
            <strong>API key niet ingesteld.</strong>{' '}
            Ga naar <a href="/boekhouding/instellingen">Instellingen</a> om je Anthropic API key in te voeren.
          </div>
        )}

        {apiKeySet && !ocrEnabled && (
          <div className={styles.alertWarning}>
            <strong>Factuur OCR is uitgeschakeld.</strong>{' '}
            Ga naar <a href="/boekhouding/instellingen">Instellingen</a> om AI factuur OCR in te schakelen.
          </div>
        )}

        {error && (
          <div className={styles.alertError}>{error}</div>
        )}

        {/* Upload area */}
        {!currentFile && (
          <div className={styles.scanUploadArea}>
            <div className={styles.scanButtons}>
              <label
                className={`${styles.scanBtn} ${styles.scanBtnPrimary}`}
                style={!ocrEnabled || !apiKeySet ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M3 9h2M19 9h2M3 15h2M19 15h2M9 3v2M15 3v2M9 19v2M15 19v2"/>
                </svg>
                <span>Maak foto</span>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>

              <label
                className={`${styles.scanBtn} ${styles.scanBtnSecondary}`}
                style={!ocrEnabled || !apiKeySet ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Upload bestand</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            <small className={styles.scanHint}>JPG, PNG of PDF - max. 10 MB</small>
          </div>
        )}

        {/* Preview */}
        {previewUrl && !isPdf && (
          <div className={styles.scanPreview}>
            <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }} />
            <button
              type="button"
              className={styles.scanPreviewRemove}
              onClick={resetUpload}
            >
              &times;
            </button>
          </div>
        )}

        {/* Crop status */}
        {cropStatus && (
          <div className={styles.cropStatus}>
            <span className={cropStatus.startsWith('✓') ? styles.scanSuccess : styles.scanWarning}>
              {cropStatus}
            </span>
          </div>
        )}

        {/* Scan status */}
        {scanning && (
          <div className={styles.scanStatus}>
            <div className={styles.scanStatusSpinner}></div>
            <span>{scanStatus || 'Bezig met scannen...'}</span>
          </div>
        )}

        {!scanning && scanStatus && (
          <div className={styles.scanStatus}>
            <span className={
              scanStatus.startsWith('✓') ? styles.scanSuccess :
              scanStatus.startsWith('⚠') ? styles.scanWarning :
              styles.scanError
            }>
              {scanStatus}
            </span>
            {nieuweRelatieSuggestie && !relatieMatch && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                onClick={openNieuweRelatieModal}
                disabled={loadingBedrijfsgegevens}
                style={{ marginLeft: '0.5rem' }}
              >
                {loadingBedrijfsgegevens ? (
                  <>
                    <span className={styles.scanStatusSpinner} style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }}></span>
                    Opzoeken...
                  </>
                ) : (
                  <>+ {nieuweRelatieSuggestie.naam} toevoegen</>
                )}
              </button>
            )}
            {factuurBestaat && (
              <a
                href={`/boekhouding/boeking/${factuurBestaat.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                style={{ marginLeft: '0.5rem' }}
              >
                Bekijk
              </a>
            )}
          </div>
        )}

        {/* Formulier */}
        {showForm && (
          <form onSubmit={handleSubmit} className={styles.form} style={{ marginTop: '1.5rem' }}>
            <h3 style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1.5rem' }}>
              Geëxtraheerde gegevens
            </h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Bkst.nr</label>
                <input
                  type="text"
                  value={volgendBoekstuknummer || '(automatisch)'}
                  onChange={(e) => setVolgendBoekstuknummer(e.target.value)}
                  placeholder="Automatisch gegenereerd"
                  disabled={!volgendBoekstuknummer}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Factuurdatum</label>
                <input
                  type="date"
                  value={formData.datum || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, datum: e.target.value }))}
                  min={`${huidigJaar}-01-01`}
                  required
                />
              </div>

              {!isVerkoop && (
                <div className={styles.formGroup}>
                  <label>Factuurnummer leverancier</label>
                  <input
                    type="text"
                    value={formData.factuurnummer || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, factuurnummer: e.target.value }))}
                    placeholder="Factuurnummer op bon"
                  />
                </div>
              )}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Bedrag excl. BTW</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.bedrag_excl || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, bedrag_excl: parseFloat(e.target.value) || 0 }));
                    setTimeout(berekenIncl, 0);
                  }}
                  placeholder="0.00"
                />
              </div>

              <div className={styles.formGroup}>
                <label>BTW bedrag</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.btw_bedrag || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, btw_bedrag: parseFloat(e.target.value) || 0 }));
                    setTimeout(berekenIncl, 0);
                  }}
                  placeholder="0.00"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Bedrag incl. BTW *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.bedrag_incl || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, bedrag_incl: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>BTW percentage</label>
                <select
                  value={formData.btw_percentage || 21}
                  onChange={(e) => setFormData(prev => ({ ...prev, btw_percentage: parseInt(e.target.value) }))}
                >
                  <option value="21">21%</option>
                  <option value="9">9%</option>
                  <option value="0">0%</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Grootboekrekening</label>
                <select
                  value={formData.grootboek || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, grootboek: e.target.value }))}
                  required
                >
                  <option value="">-- Selecteer grootboekrekening --</option>
                  {grootboeken
                    .filter(g => isVerkoop ? g.type === 'omzet' : g.type === 'inkoop')
                    .map(g => (
                      <option key={g.grootboeknaam} value={g.grootboeknaam}>
                        {g.grootboeknaam}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>{relatieLabel} (optioneel)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    value={relatieSearch}
                    onChange={(e) => {
                      setRelatieSearch(e.target.value);
                      setShowRelatieDropdown(true);
                    }}
                    onFocus={() => setShowRelatieDropdown(true)}
                    placeholder={`Zoek ${relatieLabel.toLowerCase()}...`}
                    autoComplete="off"
                  />
                  {showRelatieDropdown && (
                    <div className={styles.searchDropdown}>
                      <div
                        className={styles.searchOption}
                        onClick={() => selectRelatie(null, '')}
                      >
                        -- Geen {relatieLabel.toLowerCase()} --
                      </div>
                      {filterRelaties().map(r => (
                        <div
                          key={r.id}
                          className={styles.searchOption}
                          onClick={() => selectRelatie(r.id, r.naam)}
                        >
                          {r.naam}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSmall}`}
                  onClick={openNieuweRelatieModal}
                  title={`Nieuwe ${relatieLabel.toLowerCase()}`}
                >
                  +
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Omschrijving</label>
              <input
                type="text"
                value={formData.omschrijving || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, omschrijving: e.target.value }))}
                placeholder="Korte omschrijving..."
              />
            </div>

            <div className={styles.formActions}>
              <a
                href={`/boekhouding?boekstuk=${boekstukType}`}
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                Annuleren
              </a>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={resetUpload}
              >
                Nieuwe scan
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
              >
                Opslaan als {boekstukType}
              </button>
            </div>
          </form>
        )}

        {/* Document preview card */}
        {showForm && previewUrl && (
          <div className={styles.card} style={{ marginTop: '1.5rem' }}>
            <h3>Document</h3>
            <div>
              {isPdf ? (
                <iframe
                  src={previewUrl || ''}
                  title="Document preview"
                  style={{ width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Gescand document"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Nieuwe Relatie Modal */}
      {showNieuweRelatieModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} style={{ maxWidth: '400px' }}>
            <h3>Nieuwe {relatieLabel.toLowerCase()}</h3>
            <div className={styles.formGroup}>
              <label>Naam {relatieLabel.toLowerCase()} *</label>
              <input
                type="text"
                value={nieuweRelatieNaam}
                onChange={(e) => setNieuweRelatieNaam(e.target.value)}
                placeholder={isVerkoop ? 'Bijv. Jansen BV' : 'Bijv. MediaMarkt'}
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label>E-mail (optioneel)</label>
              <input
                type="email"
                value={nieuweRelatieEmail}
                onChange={(e) => setNieuweRelatieEmail(e.target.value)}
                placeholder="info@voorbeeld.nl"
              />
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowNieuweRelatieModal(false)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={saveNieuweRelatie}
              >
                Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}
    </BoekhoudingLayout>
  );
};

export default FactuurScan;
